import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { FileText, Calendar, ArrowLeft, Lock, Eye } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { format } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_cc23a1c1-e998-45d0-adb8-2e9c1c544201/artifacts/g7bc3tyb_image.png";

const PublicProjectView = () => {
  const { shareId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchProject();
  }, [shareId]);

  const fetchProject = async () => {
    try {
      const response = await axios.get(`${API}/public/projects/${shareId}`);
      setProject(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Project not found or link expired');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Card className="p-8 max-w-md text-center">
          <Lock className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Access Denied</h1>
          <p className="text-slate-600 mb-6">{error}</p>
          <Button onClick={() => navigate('/')} className="bg-primary text-white">
            Go to Home
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src={LOGO_URL} alt="FlowSnap" className="h-9" />
          </div>
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <Eye className="w-4 h-4" />
            <span>Public View (Read-only)</span>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {/* Document Header */}
          <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-8 mb-8">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-14 h-14 rounded-lg bg-primary/10 flex items-center justify-center">
                <FileText className="w-7 h-7 text-primary" />
              </div>
              <div className="flex-1">
                <h1 className="text-3xl font-bold text-slate-900 mb-2">
                  {project.title || project.name}
                </h1>
                {project.description && (
                  <p className="text-lg text-slate-600">{project.description}</p>
                )}
              </div>
            </div>

            <div className="flex items-center gap-6 text-sm text-slate-500">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                <span>
                  {project.created_at && format(new Date(project.created_at), 'MMMM dd, yyyy')}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4" />
                <span>{project.steps?.length || 0} steps</span>
              </div>
            </div>
          </div>

          {/* Steps */}
          <div className="space-y-6">
            {project.steps?.map((step, index) => (
              <motion.div
                key={step.id || index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="p-6 bg-white">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                      <span className="text-white font-bold">{index + 1}</span>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-semibold text-slate-900 mb-3">
                        {step.title}
                      </h3>
                      <div 
                        className="text-slate-600 leading-relaxed mb-4 prose prose-slate"
                        dangerouslySetInnerHTML={{ __html: step.description }}
                      />
                      {step.screenshot && (
                        <div className="rounded-lg overflow-hidden border border-slate-200">
                          <img
                            src={step.screenshot}
                            alt={`Step ${index + 1}`}
                            className="w-full h-auto"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>

          {/* Footer */}
          <div className="mt-12 text-center">
            <p className="text-sm text-slate-500 mb-4">
              This document was created with FlowSnap
            </p>
            <Button 
              variant="outline"
              onClick={() => navigate('/')}
            >
              Create your own documentation
            </Button>
          </div>
        </motion.div>
      </main>
    </div>
  );
};

export default PublicProjectView;
